import React, {useEffect, useRef, useState} from 'react';
import {api} from '../../lib/api';
import {Button} from '../ui';
import type {LinkedEvidence} from './CorrectionEvidence';

export default function ReceiptEvidenceUpload({ownerId,onUploaded,onBusyChange}:{
  ownerId:string; onUploaded:(version:LinkedEvidence)=>void; onBusyChange?:(busy:boolean)=>void;
}) {
  const [open,setOpen]=useState(false),[cap,setCap]=useState<any>(null),[loading,setLoading]=useState(false);
  const [title,setTitle]=useState('Receipt evidence'),[file,setFile]=useState<File|null>(null);
  const [error,setError]=useState(''),[busy,setBusy]=useState(false);
  const operation=useRef(false),requestId=useRef(''),mounted=useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  async function showUpload(){
    if(operation.current)return;
    setOpen(true);setError('');setLoading(true);operation.current=true;
    try{const result=await api.evidence.capabilities();if(mounted.current)setCap(result);}
    catch(e:any){if(mounted.current)setError(e.message||'Could not load upload settings. Try again.');}
    finally{operation.current=false;if(mounted.current)setLoading(false);}
  }
  async function upload(){
    if(operation.current)return;
    if(!cap?.uploadsEnabled){setError(cap?.reason||'Uploads are unavailable.');return;}
    if(!file||!title.trim()){setError('Choose a receipt file and enter a document title.');return;}
    if(!file.size||file.size>cap.maxBytes){setError(`Choose a non-empty file of ${cap.maxBytes/(1024*1024)} MiB or smaller.`);return;}
    const mime=file.type||(/\.pdf$/i.test(file.name)?'application/pdf':/\.png$/i.test(file.name)?'image/png':/\.jpe?g$/i.test(file.name)?'image/jpeg':'');
    if(!cap.mimeTypes.includes(mime)){setError('Choose a file in one of the supported formats shown below.');return;}
    if(!requestId.current)requestId.current=crypto.randomUUID();
    operation.current=true;setBusy(true);setError('');onBusyChange?.(true);
    try{
      const result=await api.evidence.upload('clients',ownerId,file,title.trim(),requestId.current);
      if(!mounted.current)return;
      onUploaded({...result.version,documentId:result.documentId});
      setOpen(false);setFile(null);requestId.current='';
    }catch(e:any){if(mounted.current)setError(e.message||'Upload failed. Try again.');}
    finally{operation.current=false;if(mounted.current){setBusy(false);onBusyChange?.(false);}}
  }
  return <div className="receipt-evidence-upload">
    {!open?<Button type="button" variant="secondary" onClick={showUpload}>Upload receipt evidence</Button>:<>
      <h4>Upload a physical receipt or payment proof</h4>
      <p>The file will be saved in this purchaser’s Supporting files and linked to this receipt entry. Review the amount and reference before recording money.</p>
      {loading?<p role="status">Loading upload settings…</p>:cap?.uploadsEnabled?<>
        <div className="receipt-upload-fields">
          <label>Receipt document title<input maxLength={160} value={title} disabled={busy} placeholder="e.g. Official receipt OR-001" onKeyDown={e=>{if(e.key==='Enter')e.preventDefault();}} onChange={e=>{setTitle(e.target.value);requestId.current='';}}/></label>
          <label>Receipt evidence file<input type="file" accept={cap.mimeTypes.join(',')} disabled={busy} onChange={e=>{setFile(e.target.files?.[0]||null);requestId.current='';}}/></label>
        </div>
        <p>{cap.mimeTypes.map((m:string)=>m.split('/')[1]?.toUpperCase()).join(', ')} · maximum {cap.maxBytes/(1024*1024)} MiB. Upload a scan or clear photo of the physical receipt.</p>
        <div className="receipt-upload-actions"><Button type="button" variant="secondary" disabled={busy} onClick={upload}>{busy?'Uploading receipt…':'Upload and use this receipt'}</Button><Button type="button" variant="secondary" disabled={busy} onClick={()=>{setOpen(false);setFile(null);setError('');}}>Cancel upload</Button></div>
      </>:cap?<p role="status">{cap.reason||'Uploads are currently unavailable.'}</p>:null}
      {error&&<p role="alert">{error}</p>}
      {!loading&&!cap&&<Button type="button" variant="secondary" onClick={showUpload}>Retry upload settings</Button>}
      {!loading&&!cap?.uploadsEnabled&&<Button type="button" variant="secondary" onClick={()=>setOpen(false)}>Close upload</Button>}
    </>}
  </div>;
}
